import React from "react";
import { Col, Form, Row, Collapse } from "antd";
import _ from "underscore";
import Language from "../lib/Language";
import { Button } from "@timeedit/ui-components/components/Button/Button";
import Segmented from "@timeedit/ui-components/components/Segmented/Segmented";
import ObjectSelect, {
    TApplyColumns,
    TGetVisibleObjects,
    TObjectListColumns,
} from "./ObjectSelect";
import FieldTableRow from "./FieldTableRow";
import { User } from "../models/User";
import { TFieldDefClass, TObjectCreateClass } from "../types/api/APIClassTypes";
import {
    TFieldValuesApi,
    TMemberTransferApi,
    TObjectSelectionHandler,
    TSplitSizeApi,
} from "./SplitObjectDialogHooks";
import { Typography } from "@timeedit/ui-components/components/Typography/Typography";
import {
    LeftOutlined,
    RightOutlined,
    DoubleRightOutlined,
    DoubleLeftOutlined,
} from "@timeedit/ui-components/components/Icon/Icons/index";
import { InputNumber, Radio, Slider } from "@timeedit/ui-components/antd";
import Alert from "@timeedit/ui-components/antd/alert/Alert";
import { Label } from "@timeedit/ui-components";

const GUTTER = 0;

/** One side of the member transfer list. */
const MemberList = (props: {
    onClick: TObjectSelectionHandler;
    objectIds: number[];
    searchable?: boolean;
    emptyTop?: boolean;
    partitionMemberTypeId: number;
    user: User;
    setGetVisibleObjects: (getVisibleObjects: TGetVisibleObjects) => void;
    onColumnsChange: (columns: TObjectListColumns) => void;
    setApplyColumns: (applyColumns: TApplyColumns) => void;
}) => (
    <div style={{ flexGrow: 1 }} className="objectSelectList">
        Total: {props.objectIds.length}
        <ObjectSelect
            width={"100%"}
            setGetVisibleObjects={props.setGetVisibleObjects}
            onColumnsChange={props.onColumnsChange}
            setApplyColumns={props.setApplyColumns}
            selectedType={{ id: props.partitionMemberTypeId }}
            onObjectSearcherChange={null}
            selectedFluffyItem={null}
            onClick={props.onClick}
            onObjectInfo={null}
            addReloadFunction={null}
            onRowHover={null}
            hoverButton={null}
            modalKey={"allowSecondModal"}
            highlightSelection={true}
            allowMultiSelection={true}
            isStatic={true}
            staticSearchable={props.searchable}
            emptyTop={props.emptyTop}
            staticObjects={props.objectIds}
            user={props.user}
        />
    </div>
);

// The lists fill their column, which the surrounding row stretches to the height the
// members step has been given. .objectSelectList and the table below it size themselves
// from here, so the lists follow the window instead of a fixed height.
const listColumnStyle: React.CSSProperties = {
    display: "flex",
    flexDirection: "row",
    minHeight: 0,
    marginTop: "5px",
    marginBottom: "5px",
};

/** Split by size: two synced number inputs and a slider. */
const SizeSplit = (props: { initialSize: number; size: TSplitSizeApi }) => (
    <>
        <Row gutter={GUTTER} align="middle">
            <Col span={12}>
                <Form.Item
                    label={Language.get("nc_split_object_dialog_field_label_existing_size")}
                    labelCol={{ span: 24 }}
                    wrapperCol={{ span: 24 }}
                >
                    <InputNumber
                        min={0}
                        max={props.initialSize}
                        value={props.size.oldObjectSize}
                        onChange={props.size.setOldSize}
                    />
                </Form.Item>
            </Col>
            <Col span={12}>
                <Form.Item
                    label={Language.get("nc_split_object_dialog_field_label_new_size")}
                    labelCol={{ span: 24 }}
                    wrapperCol={{ span: 24 }}
                >
                    <InputNumber
                        min={0}
                        max={props.initialSize}
                        value={props.size.newObjectSize}
                        onChange={props.size.setNewSize}
                    />
                </Form.Item>
            </Col>
        </Row>
        <Row gutter={GUTTER} align="middle">
            <Col span={24}>
                <Slider
                    min={0}
                    max={props.initialSize}
                    value={(props.initialSize ?? 0) - (props.size.newObjectSize ?? 0)}
                    onChange={props.size.setOldSize}
                    included={false}
                    tooltip={{ open: false }}
                    marks={
                        props.initialSize
                            ? {
                                  0: {
                                      style: { transform: "none" },
                                      label: Language.get(
                                          "nc_split_object_dialog_slider_marker_existing"
                                      ),
                                  },
                                  [props.initialSize]: {
                                      style: { transform: "translateX(-100%)" },
                                      label: Language.get(
                                          "nc_split_object_dialog_slider_marker_new"
                                      ),
                                  },
                              }
                            : { 0: "" }
                    }
                />
            </Col>
        </Row>
    </>
);

const MemberSplit = (props: {
    partitionMemberTypeId: number;
    user: User;
    members: TMemberTransferApi;
}) => {
    const getVisibleAvailable = React.useRef<TGetVisibleObjects>();
    const getVisibleSelected = React.useRef<TGetVisibleObjects>();
    // Both lists show the same type, so columns picked in one are pushed straight into
    // the other rather than only taking effect the next time it is opened.
    const applyColumnsAvailable = React.useRef<TApplyColumns>();
    const applyColumnsSelected = React.useRef<TApplyColumns>();

    const moveAllVisible = (
        getVisibleObjects: TGetVisibleObjects | undefined,
        move: (visibleIds?: number[]) => void
    ) => {
        if (!getVisibleObjects) {
            move();
            return;
        }
        getVisibleObjects((objects) => move(objects.map((object) => object.id)));
    };

    return (
        // Stretched, so the two list columns get the full height the step has left over.
        <Row gutter={GUTTER} align="stretch" style={{ flex: "1 1 auto", minHeight: 0 }}>
            <Col span={11} style={listColumnStyle}>
                <MemberList
                    onClick={props.members.onAvailableSelectionChanged}
                    objectIds={props.members.availableMembers.map((obj) => obj.id)}
                    searchable
                    partitionMemberTypeId={props.partitionMemberTypeId}
                    user={props.user}
                    setGetVisibleObjects={(fn) => (getVisibleAvailable.current = fn)}
                    setApplyColumns={(fn) => (applyColumnsAvailable.current = fn)}
                    onColumnsChange={(columns) => applyColumnsSelected.current?.(columns)}
                />
            </Col>
            <Col
                span={2}
                style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    // Keep the buttons centered between the lists now that the row stretches.
                    justifyContent: "center",
                    gap: "8px",
                }}
            >
                <Button onClick={props.members.moveToSelected}>
                    <RightOutlined alt={Language.tmp("Select")} />
                </Button>
                <Button
                    onClick={() =>
                        moveAllVisible(getVisibleAvailable.current, props.members.moveAllToSelected)
                    }
                >
                    <DoubleRightOutlined alt={Language.tmp("Select all")} />
                </Button>
                <Button onClick={props.members.moveToAvailable}>
                    <LeftOutlined alt={Language.tmp("Remove")} />
                </Button>
                <Button
                    onClick={() =>
                        moveAllVisible(getVisibleSelected.current, props.members.moveAllToAvailable)
                    }
                >
                    <DoubleLeftOutlined alt={Language.tmp("Remove all")} />
                </Button>
            </Col>
            <Col span={11} style={listColumnStyle}>
                <MemberList
                    emptyTop
                    onClick={props.members.onSelectedSelectionChanged}
                    objectIds={props.members.selectedMembers.map((obj) => obj.id)}
                    partitionMemberTypeId={props.partitionMemberTypeId}
                    user={props.user}
                    setGetVisibleObjects={(fn) => (getVisibleSelected.current = fn)}
                    setApplyColumns={(fn) => (applyColumnsSelected.current = fn)}
                    onColumnsChange={(columns) => applyColumnsAvailable.current?.(columns)}
                />
            </Col>
        </Row>
    );
};

// Shared column layout for the field tables so the "Unique fields" and "More fields" sections
// line up and take the same width (label column | input column | trailing button column).
const LABEL_COLUMN_WIDTH = 150;
const FieldTableColumns = () => (
    <colgroup>
        <col style={{ width: LABEL_COLUMN_WIDTH }} />
        <col />
        <col style={{ width: 24 }} />
    </colgroup>
);

/**
 * The non-unique "more fields"
 */
const MoreFields = (props: {
    fieldDefs: TFieldDefClass[];
    partitionFieldIds: number[];
    fields: TFieldValuesApi;
}) => {
    const { otherFieldValues } = props.fields;
    const fieldDef = (id: number) => props.fieldDefs.find((f) => f.id === id);

    return (
        <table style={{ width: "100%", tableLayout: "fixed" }}>
            <FieldTableColumns />
            {otherFieldValues
                .filter((field) => !props.partitionFieldIds.includes(field.id))
                .map((field) => {
                    const def = fieldDef(field.id);
                    return def ? (
                        <FieldTableRow
                            key={`${field.id}`}
                            editable={true}
                            field={field}
                            definition={def}
                            hasExternalValue={false}
                            clearExternalValue={_.noop}
                            addField={() => props.fields.addField(field)}
                            removeField={(index: number) => props.fields.removeField(field, index)}
                            setFieldValue={(
                                event: Event | null,
                                newValue: string | null,
                                valueIndex: number
                            ) =>
                                props.fields.setFieldValue(field, event, newValue ?? "", valueIndex)
                            }
                            includeCheckbox={false}
                            useTEUI={true}
                        />
                    ) : null;
                })}
        </table>
    );
};

/** Step 1: edit the new object's fields. */
export const StepObject = (props: {
    fieldDefs: TFieldDefClass[];
    partitionFieldIds: number[];
    fields: TFieldValuesApi;
    partitionMemberTypeName: string;
}) => {
    const { newFieldValues, setNewFieldValue, uniqueFieldDisplayError, setUniqueFieldServerError } =
        props.fields;
    const fieldDef = (id: number) => props.fieldDefs.find((f) => f.id === id);

    return (
        <>
            <Row gutter={GUTTER}>
                <Col span={24}>
                    <Typography.Text size={"base"} strong style={{ marginBottom: 8 }}>
                        {Language.get("cal_selected_new_of_type", props.partitionMemberTypeName)}
                    </Typography.Text>
                    <table style={{ width: "100%", tableLayout: "fixed" }}>
                        <FieldTableColumns />
                        {newFieldValues.map((field) => {
                            const def = fieldDef(field.id);
                            return def ? (
                                <FieldTableRow
                                    key={`${field.id}`}
                                    field={field}
                                    definition={def}
                                    editable={true}
                                    error={uniqueFieldDisplayError(field.id)}
                                    setFieldValue={(_event, newValue) =>
                                        setNewFieldValue(field.id, newValue ?? "")
                                    }
                                    includeCheckbox={false}
                                    useTEUI={true}
                                    // The split always creates a new object, so there is no
                                    // object entitled to keep a value it already has — every
                                    // match found on the server is a collision.
                                    checkUniqueObjectField={true}
                                    // A taken value blocks the dialog the same way the local
                                    // rules do, so the verdict has to reach the hook.
                                    onUniqueErrorChange={(error) =>
                                        setUniqueFieldServerError(field.id, error)
                                    }
                                />
                            ) : null;
                        })}
                    </table>
                </Col>
            </Row>
            <Row gutter={GUTTER}>
                <Col span={24}>
                    <Collapse
                        className="moreFieldsCollapse"
                        ghost
                        size="small"
                        items={[
                            {
                                key: "more-fields",
                                label: Language.get("nc_split_dialog_more_fields"),
                                // Drop the panel's horizontal padding so the table lines up with
                                // the unique-fields table above instead of being inset.
                                styles: { body: { paddingInline: 0 } },
                                children: (
                                    <MoreFields
                                        fieldDefs={props.fieldDefs}
                                        partitionFieldIds={props.partitionFieldIds}
                                        fields={props.fields}
                                    />
                                ),
                            },
                        ]}
                    />
                </Col>
            </Row>
        </>
    );
};

/** Step 2: choose how to divide the object — by size or by members. */
export const StepMembers = (props: {
    object: TObjectCreateClass;
    partitionMemberTypeId: number;
    selectedMode: "size" | "members";
    setSelectedMode: (mode: "size" | "members") => void;
    initialSize: number;
    user: User;
    size: TSplitSizeApi;
    members: TMemberTransferApi;
    objectName: string;
}) => (
    <>
        {props.partitionMemberTypeId > 0 ? (
            <Row gutter={GUTTER} align="middle">
                <Col span={24} style={{ display: "flex", flexDirection: "row" }}>
                    Move members to {props.objectName}:{" "}
                    <Segmented
                        className="marginAuto"
                        onChange={(val) => props.setSelectedMode(val as "size" | "members")}
                        value={props.selectedMode}
                        options={[
                            { label: "Automatic", value: "size" },
                            {
                                label: "Manual",
                                value: "members",
                                disabled:
                                    !props.object.members || props.object.members.length === 0,
                            },
                        ]}
                    />
                </Col>
            </Row>
        ) : null}
        {props.selectedMode === "size" ? (
            <SizeSplit initialSize={props.initialSize} size={props.size} />
        ) : (
            <MemberSplit
                partitionMemberTypeId={props.partitionMemberTypeId}
                user={props.user}
                members={props.members}
            />
        )}
    </>
);

export const MAKE_RESERVATION = 1;
export const RESERVE_TO_WAITING_LIST = 2;
const MANUALLY = 3;

const getExplainerText = (mode: number, typeName: string) => {
    if (mode === MANUALLY) {
        return Language.tmp(
            `The new ${typeName} will be created automatically, but you will have to place the new reservation manually using the calendar`
        );
    }
    if (mode === MAKE_RESERVATION) {
        return Language.tmp(
            `A new reservation with the new ${typeName} will be created at the same time`
        );
    }
    return Language.tmp(
        `A new reservation with the new ${typeName} will be created on the waiting list`
    );
};

/** Step 3: whether to create a reservation for the new object. */
// Disable everything but manually if props.isCapacitySize
// Disable everything (double check) if !props.makeReservation
/*

                        {
                            value: RESERVE_TO_WAITING_LIST,
                            label: Language.get("nc_split_dialog_reserve_to_waiting_list"),
                            className: "grow",
                        },

*/
export const StepReservation = (props: {
    makeReservation: number;
    setMakeReservation: (value: number) => void;
    isCapacitySize: boolean;
    partitionMemberTypeName: string;
}) => (
    <>
        <Row gutter={GUTTER} align="middle">
            <Col span={24} style={{ textAlign: "left" }}>
                <Label
                    title={Language.tmp("Create new reservation")}
                    tooltip={getExplainerText(props.makeReservation, props.partitionMemberTypeName)}
                />
                <Radio.Group
                    style={{ display: "flex", flexDirection: "row" }}
                    value={props.makeReservation}
                    onChange={(event) => props.setMakeReservation(event?.target.value)}
                    options={[
                        {
                            value: MAKE_RESERVATION,
                            label: Language.tmp("At same time"),
                            className: "grow",
                        },
                        {
                            value: MANUALLY,
                            label: Language.tmp("Place manually (using calendar)"),
                            className: "grow",
                        },
                    ]}
                />
            </Col>
        </Row>
        <Row gutter={GUTTER} align="middle">
            <Col span={24}>
                <Alert
                    showIcon
                    type="info"
                    closable={false}
                    description={getExplainerText(
                        props.makeReservation,
                        props.partitionMemberTypeName
                    )}
                />
            </Col>
        </Row>
    </>
);
